import { useCallback, useState, useImperativeHandle, useEffect } from "react";
import { Button, Input, Space } from "antd";
import Icon, { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { FaSearch } from 'react-icons/fa';
import { removeAcento } from '../../utils/RemoveAcentos';

interface IProps {
    dataIndex: any,
    ref: any,
    formatter?: (text: string) => string;
    initialValue?: string | null;
    onFilterClear?: (dataIndex: string) => void;
}

const IconSearch = (props: any) => <Icon {...props} component={FaSearch} />;

export const useGetColumnSearchProps = ({
    dataIndex,
    ref,
    formatter,
    initialValue,
    onFilterClear,
}: IProps) => {

    const [searchText, setSearchText] = useState(initialValue || '');
    const [searchedColumn, setSearchedColumn] = useState(initialValue ? dataIndex : '');

    useEffect(() => {
        setSearchText(initialValue || '');
        setSearchedColumn(initialValue ? dataIndex : '');
    }, [initialValue, dataIndex]);

    const handleSearch = useCallback((selectedKeys: any, confirm: any, dataIndex: any): void => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((): void => {
        setSearchText('');
        setSearchedColumn('');
        onFilterClear?.(dataIndex);
    }, [onFilterClear, dataIndex]);

    useImperativeHandle(ref, () => ({
        limparFiltros() {
            handleReset();
        }
    }));

    let searchInput: any = null;

    return {
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {

            const currentValue = selectedKeys[0] || searchText;

            return (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={node => { searchInput = node; }}
                        placeholder="Digite..."
                        value={currentValue}
                        onChange={(e: any) => {
                            const value = formatter ? formatter(e.target.value) : e.target.value;

                            setSelectedKeys(value ? [value] : []);
                        }}
                        onPressEnter={(e) => {
                            e.stopPropagation();
                            handleSearch(selectedKeys, confirm, dataIndex);
                        }}
                        style={{ width: 208, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            onClick={() => {
                                clearFilters();
                                confirm();
                                handleReset();
                            }}
                            size="small"
                            style={{ width: 100 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            )
        },
        filterIcon: (filtered: any) => filtered ?
            <IconSearch style={{ color: '#1890ff', fontSize: 14 }} /> :
            <SearchOutlined />,

        onFilterDropdownOpenChange: (visible: boolean) => {
            if (visible) {
                setTimeout(() => searchInput?.focus?.() || searchInput?.select?.(), 100);
            }
        },

        onFilter: (value: any, record: any) => {
            const rawValue = record[dataIndex];
            if (!rawValue) return false;

            const formatted = formatter ? formatter(rawValue.toString()) : rawValue.toString();
            return removeAcento(formatted).toLowerCase().includes(removeAcento(value).toLowerCase());
        },

        filteredValue: searchText ? [searchText] : null,

        render: (text: any) => {
            const displayText = text ? (formatter ? formatter(text.toString()) : text.toString()) : '--';

            return searchText && searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={displayText}
                />
            ) : (
                displayText
            );
        }
    };
};
